if (!customElements.get('product-upsell')) {
  customElements.define(
    'product-upsell',
    class ProductUpsell extends HTMLElement {
      constructor() {
        super();
        
        this.sectionId = this.dataset.sectionId;
        this.mainProductId = this.dataset.mainProductId;
        this.upsellProductId = this.dataset.upsellProductId;
        this.upsellProductHandle = this.dataset.upsellProductHandle;
        
        this.currentMainVariantId = null;
        this.currentMainVariantTitle = null;
        this.selectedUpsellVariantId = null;
        this.isEnabled = false;
        this.shouldAddUpsell = false;
        this.variantChangeUnsubscriber = undefined;
        this.cartUpdateUnsubscriber = undefined;
        this.cartErrorUnsubscriber = undefined;
      }

      parseUpsellVariants() {
        const variantsJson = this.querySelector('.product-upsell__upsell-variants-json');
        if (!variantsJson) return [];
        
        try {
          return JSON.parse(variantsJson.textContent);
        } catch (e) {
          console.error('Error parsing upsell variants:', e);
          return [];
        }
      }

      connectedCallback() {
        this.toggleInput = this.querySelector('.product-upsell__toggle-input');
        this.upsellVariants = this.parseUpsellVariants();
        
        // Get initial variant
        this.updateCurrentVariant();
        
        // Listen to toggle changes
        if (this.toggleInput) {
          this.toggleInput.addEventListener('change', this.handleToggleChange.bind(this));
          // If toggle is already checked, set initial state
          if (this.toggleInput.checked) {
            this.isEnabled = true;
            if (!this.selectedUpsellVariantId && this.currentMainVariantTitle) {
              this.findMatchingUpsellVariant(this.currentMainVariantTitle);
            }
          }
        }

        // Listen to variant changes
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data && event.data.sectionId === this.sectionId) {
            this.handleMainVariantChange(event.data.variant);
          }
        });
        
        // Initialize subtotal on load if toggle is already enabled
        if (this.toggleInput && this.toggleInput.checked) {
          setTimeout(() => {
            this.updateSubtotal();
          }, 200);
        }

        // Listen to cart updates to add upsell after main product is added
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          console.log('Cart update event received:', event.source, 'shouldAddUpsell:', this.shouldAddUpsell, 'isEnabled:', this.isEnabled, 'variantId:', this.selectedUpsellVariantId);
          
          if (event.source === 'product-form' && this.shouldAddUpsell && this.isEnabled && this.selectedUpsellVariantId) {
            this.shouldAddUpsell = false; // Reset flag to prevent multiple additions
            // Small delay to ensure main product is fully added
            setTimeout(() => {
              this.addUpsellToCart();
            }, 300);
          }
        });
        
        // Also listen for cart errors to reset flag
        this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
          if (event.source === 'product-form') {
            // If main product add failed, don't try to add upsell
            this.shouldAddUpsell = false;
          }
        });

        // Intercept form submission to set flag
        this.interceptFormSubmission();
      }

      updateCurrentVariant() {
        // Get current variant from variant selects
        const variantSelects = document.querySelector(`#variant-selects-${this.sectionId}`);
        if (variantSelects) {
          const selectedVariantScript = variantSelects.querySelector('[data-selected-variant]');
          if (selectedVariantScript) {
            try {
              const variantData = JSON.parse(selectedVariantScript.textContent);
              this.currentMainVariantId = variantData.id;
              this.currentMainVariantTitle = variantData.title;
              return variantData;
            } catch (e) {
              console.error('Error parsing variant data:', e);
            }
          }
        }
        
        // Fallback: try to get from hidden input
        const variantInput = document.querySelector(`#ProductInfo-${this.sectionId} .product-variant-id`);
        if (variantInput && variantInput.value) {
          this.currentMainVariantId = parseInt(variantInput.value);
        }
        
        return null;
      }

      interceptFormSubmission() {
        const productForm = document.querySelector(`#ProductInfo-${this.sectionId} product-form`);
        if (!productForm) {
          console.warn('Product form not found for upsell');
          return;
        }

        const form = productForm.querySelector('form');
        if (!form) {
          console.warn('Form element not found for upsell');
          return;
        }

        // Listen before the product-form handler
        form.addEventListener('submit', (e) => {
          // Update current variant before checking
          this.updateCurrentVariant();
          
          // If toggle is on but no variant selected yet, try to find one
          if (this.toggleInput && this.toggleInput.checked && !this.selectedUpsellVariantId && this.currentMainVariantTitle) {
            this.findMatchingUpsellVariant(this.currentMainVariantTitle);
          }
          
          // Set flag to add upsell after main product is successfully added
          if (this.toggleInput && this.toggleInput.checked && this.selectedUpsellVariantId) {
            this.shouldAddUpsell = true;
            console.log('Upsell will be added. Variant ID:', this.selectedUpsellVariantId);
          } else {
            this.shouldAddUpsell = false;
            if (this.toggleInput && this.toggleInput.checked && !this.selectedUpsellVariantId) {
              console.warn('Upsell toggle is on but no variant selected');
            }
          }
        }, true);
      }

      handleToggleChange(event) {
        this.isEnabled = event.target.checked;
        
        // If disabling, clear selected variant
        if (!this.isEnabled) {
          this.selectedUpsellVariantId = null;
          this.updateSubtotal();
        } else {
          // If enabling, update current variant and find matching upsell variant
          this.updateCurrentVariant();
          if (this.currentMainVariantTitle) {
            this.findMatchingUpsellVariant(this.currentMainVariantTitle);
          } else {
            // Fallback: use first available variant
            const firstAvailable = this.upsellVariants.find(v => v.available);
            this.selectedUpsellVariantId = firstAvailable ? firstAvailable.id : null;
            if (this.selectedUpsellVariantId) {
              console.log('Using fallback upsell variant:', this.selectedUpsellVariantId);
            }
          }
          this.updateSubtotal();
        }
      }
      
      updateSubtotal() {
        // Get subtotal element
        const subtotalElement = document.querySelector(`#subtotal-${this.sectionId} .product__subtotal-content`);
        if (!subtotalElement) return;
        
        // Get current variant from variant-selects to get accurate price
        let mainPriceCents = 0;
        let comparePriceCents = 0;
        
        const variantSelects = document.querySelector(`#variant-selects-${this.sectionId}`);
        if (variantSelects) {
          const selectedVariantScript = variantSelects.querySelector('[data-selected-variant]');
          if (selectedVariantScript) {
            try {
              const variantData = JSON.parse(selectedVariantScript.textContent);
              mainPriceCents = variantData.price || 0;
              comparePriceCents = variantData.compare_at_price || 0;
            } catch (e) {
              console.error('Error parsing variant data for subtotal:', e);
              return;
            }
          }
        }
        
        // Fallback: try to get from price element
        if (!mainPriceCents) {
          const priceElement = document.querySelector(`#price-${this.sectionId} .price-item--regular, #price-${this.sectionId} .price-item--sale`);
          if (priceElement) {
            const mainPriceText = priceElement.textContent.trim();
            mainPriceCents = this.parsePriceToCents(mainPriceText);
          }
        }
        
        if (!mainPriceCents) return;
        
        // Get upsell price if enabled (from metafield discounted price)
        let upsellPriceCents = 0;
        if (this.isEnabled) {
          const upsellPriceEl = this.querySelector('.product-upsell__offer-price');
          if (upsellPriceEl) {
            const upsellPriceText = upsellPriceEl.textContent.trim();
            upsellPriceCents = this.parsePriceToCents(upsellPriceText);
          }
        }
        
        // Calculate total
        const totalCents = mainPriceCents + (upsellPriceCents || 0);
        //const totalPriceText = this.formatPrice(totalCents);
        const totalPriceText = this.formatPriceWithSeparator(totalCents); // suresh.katiki
        
        // Update subtotal price
        const subtotalPriceEl = subtotalElement.querySelector('.product__subtotal-price');
        if (subtotalPriceEl) {
          subtotalPriceEl.textContent = totalPriceText;
        }
        
        // Update compare price if exists
        if (comparePriceCents > mainPriceCents) {
          const totalCompareCents = comparePriceCents + (upsellPriceCents || 0);
          //const totalCompareText = this.formatPrice(totalCompareCents);
          const totalCompareText = this.formatPriceWithSeparator(totalCompareCents); // suresh.katiki
          
          let subtotalCompareEl = subtotalElement.querySelector('.product__subtotal-old-price');
          if (!subtotalCompareEl && totalCompareCents > totalCents) {
            subtotalCompareEl = document.createElement('span');
            subtotalCompareEl.className = 'product__subtotal-old-price';
            const subtotalPriceEl = subtotalElement.querySelector('.product__subtotal-price');
            if (subtotalPriceEl && subtotalPriceEl.parentNode) {
              subtotalPriceEl.parentNode.insertBefore(subtotalCompareEl, subtotalPriceEl);
            }
          }
          if (subtotalCompareEl && totalCompareCents > totalCents) {
            subtotalCompareEl.textContent = totalCompareText;
            subtotalCompareEl.style.display = '';
          } else if (subtotalCompareEl && totalCompareCents <= totalCents) {
            subtotalCompareEl.style.display = 'none';
          }
          
          // Update savings badge
          const savings = totalCompareCents - totalCents;
          if (savings > 0) {
            let savingsBadge = subtotalElement.querySelector('.product__subtotal-save-badge');
            if (!savingsBadge) {
              savingsBadge = document.createElement('span');
              savingsBadge.className = 'product__subtotal-save-badge';
              const subtotalLabel = subtotalElement.querySelector('.product__subtotal-label');
              if (subtotalLabel && subtotalLabel.parentNode) {
                subtotalLabel.parentNode.insertBefore(savingsBadge, subtotalLabel.nextSibling);
              }
            }
            savingsBadge.textContent = 'SAVE ' + this.formatPrice(savings);
            //savingsBadge.style.display = '';
            savingsBadge.style.display = 'none'; // to hide it on the page - suresh.katiki
          } else {
            const savingsBadge = subtotalElement.querySelector('.product__subtotal-save-badge');
            if (savingsBadge) {
              savingsBadge.style.display = 'none';
            }
          }
        } else {
          // Hide compare price and savings if no compare price
          const subtotalCompareEl = subtotalElement.querySelector('.product__subtotal-old-price');
          if (subtotalCompareEl) {
            subtotalCompareEl.style.display = 'none';
          }
          const savingsBadge = subtotalElement.querySelector('.product__subtotal-save-badge');
          if (savingsBadge) {
            savingsBadge.style.display = 'none';
          }
        }
      }
      
      parsePriceToCents(priceText) {
        if (!priceText) return 0;
        // Remove currency symbols, whitespace, and commas
        let cleanPrice = priceText.replace(/[^\d.,]/g, '').replace(/,/g, '');
        // Convert to cents (assume formatted as dollars)
        const price = parseFloat(cleanPrice);
        if (isNaN(price)) return 0;
        return Math.round(price * 100);
      }
      
      formatPrice(cents) {
        if (typeof window.Shopify !== 'undefined' && window.Shopify.formatMoney) {
          const formatted = window.Shopify.formatMoney(cents, window.money_format || '{{amount}}');
          // Remove trailing zeros
          return formatted.replace(/\.00(?=\s|,|$)/g, '').replace(/\.0(?=\s|,|$)/g, '');
        }
        // Fallback formatting
        const formatted = '$' + (cents / 100).toFixed(2);
        return formatted.replace(/\.00(?=\s|,|$)/g, '').replace(/\.0(?=\s|,|$)/g, '');
      }
      // suresh.katiki
      formatPriceWithSeparator(cents) {
        var formatted;
        if (typeof window.Shopify !== 'undefined' && window.Shopify.formatMoney) {
          formatted = window.Shopify.formatMoney(cents, window.money_format || '{{amount}}');
        } else {
          // Fallback formatting
          formatted = '$' + (cents / 100).toFixed(2);
        }
        // Remove trailing zeros (.00, .0) at end or before whitespace/comma
        formatted = formatted.replace(/\.00(?=\s|,|$)/g, '');
        formatted = formatted.replace(/\.0(?=\s|,|$)/g, '');

        formatted = formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ","); // Add thousand separators
        return formatted;
      }

      handleMainVariantChange(variant) {
        if (!variant) return;
        
        this.currentMainVariantId = variant.id;
        this.currentMainVariantTitle = variant.title;
        
        // If toggle is on, find matching upsell variant
        if (this.isEnabled && variant.title) {
          this.findMatchingUpsellVariant(variant.title);
        }
        
        // Update subtotal when variant changes (if upsell is enabled)
        if (this.isEnabled) {
          setTimeout(() => {
            this.updateSubtotal();
          }, 100);
        }
      }

      findMatchingUpsellVariant(mainVariantTitle) {
        if (!mainVariantTitle) {
          // Try to get from current variant data
          const variantSelects = document.querySelector(`#variant-selects-${this.sectionId}`);
          if (variantSelects) {
            const selectedVariantScript = variantSelects.querySelector('[data-selected-variant]');
            if (selectedVariantScript) {
              try {
                const variantData = JSON.parse(selectedVariantScript.textContent);
                mainVariantTitle = variantData.title;
              } catch (e) {
                console.error('Error parsing variant data:', e);
              }
            }
          }
        }

        if (!mainVariantTitle) {
          console.warn('Could not determine main variant title for matching');
          // Fallback: use first available variant
          const firstAvailable = this.upsellVariants.find(v => v.available);
          this.selectedUpsellVariantId = firstAvailable ? firstAvailable.id : null;
          return;
        }

        // Normalize titles for matching (remove extra spaces, convert to lowercase)
        const normalizeTitle = (title) => {
          return title.toLowerCase().trim().replace(/\s+/g, ' ');
        };

        const normalizedMainTitle = normalizeTitle(mainVariantTitle);

        // Find upsell variant with matching title
        // Priority: exact match > contains match > first available
        let matchingVariant = this.upsellVariants.find(variant => {
          if (!variant.available) return false;
          return normalizeTitle(variant.title) === normalizedMainTitle;
        });

        if (!matchingVariant) {
          // Try partial match - check if main title contains upsell title or vice versa
          matchingVariant = this.upsellVariants.find(variant => {
            if (!variant.available) return false;
            const normalizedUpsellTitle = normalizeTitle(variant.title);
            return normalizedUpsellTitle.includes(normalizedMainTitle) ||
                   normalizedMainTitle.includes(normalizedUpsellTitle);
          });
        }

        if (matchingVariant) {
          this.selectedUpsellVariantId = matchingVariant.id;
        } else {
          // Fallback: use first available variant
          const firstAvailable = this.upsellVariants.find(v => v.available);
          this.selectedUpsellVariantId = firstAvailable ? firstAvailable.id : null;
        }
      }

      async addUpsellToCart() {
        if (!this.selectedUpsellVariantId) {
          console.warn('No upsell variant selected');
          return;
        }

        if (!this.isEnabled) {
          console.warn('Upsell is disabled, not adding to cart');
          return;
        }

        console.log('Adding upsell variant to cart:', this.selectedUpsellVariantId);

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData();
        formData.append('id', this.selectedUpsellVariantId);
        formData.append('quantity', '1');

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cart && typeof cart.getSectionsToRender === 'function') {
          formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
          formData.append('sections_url', window.location.pathname);
        }

        config.body = formData;

        try {
          const response = await fetch(`${routes.cart_add_url}`, config);
          const data = await response.json();

          if (data.status) {
            console.error('Error adding upsell to cart:', data.description);
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-upsell',
              productVariantId: this.selectedUpsellVariantId,
              errors: data.errors || data.description,
              message: data.message,
            });
          } else {
            console.log('Upsell added to cart successfully');
            // Update cart UI
            if (cart && typeof cart.renderContents === 'function') {
              cart.renderContents(data);
            }
            
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-upsell',
              productVariantId: this.selectedUpsellVariantId,
              cartData: data,
            });
          }
        } catch (error) {
          console.error('Error adding upsell to cart:', error);
        }
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }
      }
    }
  );
}

